import { useBoolean } from '@/hooks/state/use-boolean';
import Iconify from '@/components/media/iconify';
import CustomPopover, { usePopover } from '@/components/overlays/custom-popover';
import { ConfirmDialog } from '@/components/overlays/custom-dialog';
import { useTranslation } from 'react-i18next';
import { fillWithZeros } from '@/utils/formatters/format-number';
import Label from '@/components/core/label/label';
import Typography from '@mui/material/Typography';
import { ShowIf } from '@/utils/ui/showIf';
import { IOrderItem, ChoicesOrderStatus } from '@/types/order';
import {
  Box,
  Stack,
  Avatar,
  Button,
  MenuItem,
  TableRow,
  TableCell,
  IconButton,
} from '@mui/material';
import moment from 'moment';
import { ICountryItem } from '@/types';
import { ReceiptLong } from '@mui/icons-material';
import { useMemo, useState, useCallback } from 'react';
import OrderStatusWithTooltip from '@/components/data-display/order-status-with-tooltip';
import { DialogCancelMilestones } from '../dialogs/dialog-cancel-milestones';

type Props = {
  selected: boolean;
  row: IOrderItem;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction;
  onBill: VoidFunction;
  userPermissions: {
    canView: any;
    canUpdate: any;
    canDelete: any;
    canCancel: boolean;
  };
  isUnderLargeScreen: boolean;
};

export function OrderTableRow({
  row,
  selected,
  onEditRow,
  onDeleteRow,
  onBill,
  userPermissions,
  isUnderLargeScreen,
}: Props) {
  const { t } = useTranslation();
  const confirm = useBoolean();
  const popover = usePopover();

  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  // Memoize confirm dialog handlers
  const handleConfirmDelete = useCallback(() => {
    confirm.onTrue();
    popover.onClose();
  }, [confirm, popover]);

  const handleEditClick = useCallback(() => {
    onEditRow();
    popover.onClose();
  }, [onEditRow, popover]);

  const handleBillClick = useCallback(() => {
    onBill();
    popover.onClose();
  }, [onBill, popover]);

  // Memoize edit action based on permissions
  const editAction = useMemo(
    () =>
      userPermissions.canUpdate || userPermissions.canView
        ? { style: { cursor: 'pointer' }, onClick: () => onEditRow() }
        : {},
    [userPermissions.canUpdate, userPermissions.canView, onEditRow]
  );

  // Memoize country information
  const country: ICountryItem = useMemo(
    () => row?.organization?.country as unknown as ICountryItem,
    [row?.organization?.country]
  );

  // Memoize billing conditions
  const conditionsBillingPending = useMemo(
    () => (!row?.billingStatus && !row.legalEntity) || (row?.OVNumber && !row?.salesOrders?.length),
    [row?.billingStatus, row.legalEntity, row?.OVNumber, row?.salesOrders]
  );

  // Memoize submitted by info
  const submittedByInfo = useMemo(() => {
    if (!row?.submittedBy) return null;

    return {
      fullName: `${row.submittedBy?.firstName} ${row.submittedBy?.lastName}`,
      avatarUrl: row.submittedBy?.avatar?.url ? decodeURI(row.submittedBy.avatar.url) : '',
      email: row.submittedBy?.email ?? '',
    };
  }, [row?.submittedBy]);

  // Memoize submitted at info
  const submittedAtInfo = useMemo(() => {
    if (!row?.submittedAt) return null;

    return {
      formattedDate: moment(row.submittedAt).utc().format('LL'),
      humanizedDuration: moment.duration(moment(row.submittedAt).diff(moment())).humanize(true),
    };
  }, [row?.submittedAt]);

  // Memoize order number display
  const orderNumberDisplay = useMemo(
    () => (
      <Stack direction="column">
        <strong>#{fillWithZeros(row.number, 5)}</strong>
        {row.isMarket && (
          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
            Marketplace
          </Typography>
        )}
      </Stack>
    ),
    [row.number, row.isMarket]
  );

  // Memoize organization display
  const organizationDisplay = useMemo(
    () => (
      <Stack
        direction="row"
        flex={1}
        alignItems="center"
        sx={{
          color: 'text.primary',
          minWidth: '200px',
          maxWidth: '200px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {country && (
            <Iconify
              icon={`flagpack:${country.flag_image}`}
              sx={{ borderRadius: 0.65, width: 24, height: 18, flexShrink: 0 }}
            />
          )}
          <Box sx={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: '600',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.primary',
              }}
              title={row?.organization?.name}
            >
              {row?.organization?.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
              title={row?.organization?.segment?.name}
            >
              {row?.organization?.segment?.name}
            </Typography>
          </Box>
        </Box>
      </Stack>
    ),
    [country, row?.organization?.name, row?.organization?.segment]
  );

  // Memoize items count display
  const itemsCountDisplay = useMemo(() => row.items.length, [row.items.length]);

  // Memoize total amount display
  const totalAmountDisplay = useMemo(() => {
    const amount = row?.salesOrders?.[0]?.totalAmount;
    if (!Number.isFinite(amount as number)) {
      return '-';
    }
    const num = amount as number;
    return Number.isInteger(num) ? num.toString() : num.toFixed(2);
  }, [row?.salesOrders]);

  // Memoize submitted by display
  const submittedByDisplay = useMemo(
    () =>
      submittedByInfo ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Avatar
            alt={submittedByInfo.fullName}
            src={submittedByInfo.avatarUrl}
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
            imgProps={{
              loading: 'lazy',
              sx: {
                transform: 'scale(1.2)',
                objectFit: 'cover',
              },
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                typography: 'body2',
                fontWeight: 500,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {submittedByInfo.fullName}
            </Box>
            <Box
              sx={{
                typography: 'caption',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {submittedByInfo.email}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            typography: 'body2',
            color: 'text.secondary',
            textAlign: 'center',
          }}
        />
      ),
    [submittedByInfo]
  );

  // Memoize billing status display
  const billingStatusDisplay = useMemo(
    () =>
      row.status !== ChoicesOrderStatus.created && conditionsBillingPending ? (
        <Label variant="soft" color="warning">
          {t('Pending')}
        </Label>
      ) : (
        ''
      ),
    [row.status, conditionsBillingPending, t]
  );

  // Memoize submitted at display
  const submittedAtDisplay = useMemo(
    () =>
      submittedAtInfo ? (
        <Stack direction="column" flexWrap="wrap" color="text.white">
          {submittedAtInfo.formattedDate}
          <Typography variant="caption" color="text.secondary">
            {submittedAtInfo.humanizedDuration}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" />
      ),
    [submittedAtInfo]
  );

  const { approvalResponsibles, provisionResponsibles } = row;

  // Memoize order status display
  const orderStatusDisplay = useMemo(
    () => (
      <OrderStatusWithTooltip
        status={row.status as ChoicesOrderStatus}
        approvalResponsibles={approvalResponsibles ?? []}
        provisionResponsibles={provisionResponsibles ?? []}
        hasSyncError={row.hasSyncError}
      />
    ),
    [row.status, approvalResponsibles, provisionResponsibles, row.hasSyncError]
  );

  // Memoize action menu conditions
  const canShowBillAction = useMemo(
    () =>
      userPermissions.canUpdate &&
      row.status === ChoicesOrderStatus.completed &&
      conditionsBillingPending &&
      !row.salesOrders?.some((order) => !!order.legalEntity),
    [userPermissions.canUpdate, row.status, conditionsBillingPending, row.salesOrders]
  );

  const canShowDeleteAction = useMemo(
    () => userPermissions.canDelete && [ChoicesOrderStatus.created.toString()].includes(row.status),
    [userPermissions.canDelete, row.status]
  );

  return (
    <>
      <TableRow hover selected={selected}>
        <TableCell {...editAction}>{orderNumberDisplay}</TableCell>

        <TableCell {...editAction}>{organizationDisplay}</TableCell>

        <ShowIf condition={!isUnderLargeScreen}>
          <TableCell width="80px" align="center" {...editAction}>
            {itemsCountDisplay}
          </TableCell>
        </ShowIf>

        <TableCell align="center">{billingStatusDisplay}</TableCell>

        <TableCell {...editAction}>{submittedByDisplay}</TableCell>

        <TableCell sx={{ px: 1, whiteSpace: 'nowrap' }}>{submittedAtDisplay}</TableCell>
        <TableCell align="center">
          {Number(totalAmountDisplay) > 0 ? `$${totalAmountDisplay}` : ''}
        </TableCell>
        <TableCell align="center" {...editAction}>
          {orderStatusDisplay}
        </TableCell>

        <ShowIf
          condition={userPermissions.canUpdate || userPermissions.canDelete}
          alternative={<TableCell>&nbsp;</TableCell>}
        >
          <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
            {(userPermissions.canUpdate || userPermissions.canDelete) &&
            row.status !== 'deleted' ? (
              <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
                <Iconify icon="eva:more-vertical-fill" />
              </IconButton>
            ) : null}
          </TableCell>
        </ShowIf>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 160 }}
      >
        <ShowIf condition={canShowBillAction}>
          <MenuItem onClick={handleBillClick}>
            <ReceiptLong />
            {t('Bill')}
          </MenuItem>
        </ShowIf>

        <ShowIf condition={canShowDeleteAction}>
          <MenuItem onClick={handleConfirmDelete} sx={{ color: 'error.main' }}>
            <Iconify icon="solar:trash-bin-trash-bold" />
            {t('Delete')}
          </MenuItem>
        </ShowIf>

        <ShowIf condition={userPermissions.canUpdate}>
          <MenuItem onClick={handleEditClick}>
            <Iconify icon="solar:pen-bold" />
            {t('Edit')}
          </MenuItem>
        </ShowIf>

        <ShowIf condition={userPermissions.canCancel && !conditionsBillingPending}>
          <MenuItem
            onClick={() => {
              setOpenCancelDialog(true);
              popover.onClose();
            }}
          >
            <Iconify icon="mdi:cancel" />
            {t('Cancel Order')}
          </MenuItem>
        </ShowIf>
      </CustomPopover>

      <ShowIf condition={userPermissions.canDelete && confirm.value}>
        <ConfirmDialog
          open={confirm.value}
          onClose={confirm.onFalse}
          title={t('Delete')}
          content={t('Are you sure want to delete Order #"{{number}}"?', { number: row.number })}
          action={
            <Button variant="contained" color="error" onClick={onDeleteRow}>
              {t('Delete')}
            </Button>
          }
        />
      </ShowIf>

      <DialogCancelMilestones
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        orderId={row._id}
      />
    </>
  );
}
